import { SimpleTelemetryLogger, ISimpleTelemetryLogger } from './SimpleTelemetryLogger';
import { ActivityScope, IActivityScope } from './Activity';
import { DataField } from './DataField';
import { OptionalEventFlags } from './OptionalEventFlags';
import { Contracts } from './contracts/Contracts';

export interface ErrorEvent {
  eventName: string;
  error: Contracts.Office.System.Error.Contract;
  eventFlags?: OptionalEventFlags;
  dataFields?: DataField[];
}

export interface ITelemetryLogger extends ISimpleTelemetryLogger {
  /**
   * Starts an unparented activity to execute asynchronously
   * The caller must call setResult or setSuccess
   * Returns the result of the activityBody
   * @param activityName - The name of the activity
   * @param activityBody - The body of the activity
   */
  executeActivityAsync<T>(activityName: string, activityBody: (activity: IActivityScope) => Promise<T>): Promise<T>;

  /**
   * Starts an unparented activity to execute synchronously
   * The caller must call setResult or setSuccess
   * Returns the result of the activityBody
   * @param activityName - The name of the activity
   * @param activityBody - The body of the activity
   */
  executeActivitySync<T>(activityName: string, activityBody: (activity: IActivityScope) => T): T;

  /**
   * Creates a new activity. The caller is reponsible for setting the result and calling endNow.
   * @param activityName - The name of the activity
   */
  createNewActivity(activityName: string, parent?: IActivityScope): IActivityScope;

  /**
   * Advanced usage: Sends an activity. The caller is reponsible for setting all of the activity parameters.
   * @param activityName - The name of the activity
   * @param activity - The activity contract
   * @param dataFields - Optional additional data fields
   * @param optionalEventFlags - Optional event flags
   */
  sendActivity(
    activityName: string,
    activity: Contracts.Office.System.Activity.Contract,
    dataFields?: DataField[],
    optionalEventFlags?: OptionalEventFlags
  ): void;

  /**
   * Sends an event with the Error data contract. A convenience wrapper around sendTelemetryEvent, with
   * an Error as a data field.
   * @param eventName - The name of the event
   * @param errorGroup - The group of the error
   * @param tag - The error tag
   * @param code - The error code
   * @param id - The error id
   * @param count - The count of the error
   * @param dataFields - Data fields to send with the event
   * @param eventFlags - Optional event flags to use for the error
   */
  sendError(event: ErrorEvent): void;
}

/**
 * A TelemetryLogger that includes more advanced functions than the SimpleTelemetryLogger,
 * specifically activities and errors.
 */
export class TelemetryLogger extends SimpleTelemetryLogger implements ITelemetryLogger {
  /**
   * Starts an unparented activity to execute asynchronously
   * The caller must call setResult or setSuccess
   * Returns the result of the activityBody
   * @param activityName - The name of the activity
   * @param activityBody - The body of the activity
   */
  async executeActivityAsync<T>(activityName: string, activityBody: (activity: IActivityScope) => Promise<T>): Promise<T> {
    return this.createNewActivity(activityName).executeAsync(activityBody);
  }

  /**
   * Starts an unparented activity to execute synchronously
   * The caller must call setResult or setSuccess
   * Returns the result of the activityBody
   * @param activityName - The name of the activity
   * @param activityBody - The body of the activity
   */
  public executeActivitySync<T>(activityName: string, activityBody: (activity: IActivityScope) => T): T {
    return this.createNewActivity(activityName).executeSync(activityBody);
  }

  /**
   * Creates a new activity. The caller is responsible for setting the result and calling endNow.
   * @param activityName - The name of the activity
   */
  public createNewActivity(activityName: string): IActivityScope {
    return ActivityScope.createNew(this, activityName);
  }

  /**
   * Advanced usage: Sends an activity. The caller is responsible for setting all of the activity parameters.
   * @param activityName - The name of the activity
   * @param activity - The activity contract
   * @param dataFields - Optional additional data fields
   * @param optionalEventFlags - Optional event flags
   */
  public sendActivity(
    activityName: string,
    activity: Contracts.Office.System.Activity.Contract,
    dataFields?: DataField[],
    optionalEventFlags?: OptionalEventFlags
  ) {
    return this.sendTelemetryEvent({
      eventName: activityName,
      eventContract: {
        name: Contracts.Office.System.Activity.contractName,
        dataFields: Contracts.Office.System.Activity.getFields(activity)
      },
      dataFields: dataFields,
      eventFlags: optionalEventFlags
    });
  }

  /**
   * Sends an event with the Error data contract. A convenience wrapper around sendTelemetryEvent, with
   * an Error as a data field.
   * @param error - an ErrorEvent that represents the error
   */
  public sendError(error: ErrorEvent) {
    let dataFields: DataField[] = Contracts.Office.System.Error.getFields('Error', error.error);
    if (error.dataFields != null) {
      dataFields.push(...error.dataFields);
    }
    return this.sendTelemetryEvent({
      eventName: error.eventName,
      dataFields: dataFields,
      eventFlags: error.eventFlags
    });
  }
}
